/**
 * /admin/menu — full menu CRUD: categories on one tab, items on another.
 * Inline edit isn't supported by design — each row opens a modal so the
 * full customization editor is always visible.
 */
import { useEffect, useState } from 'react';
import type {
  CreateCategoryInput,
  CreateMenuItemInput,
  MenuCategory,
  MenuItem,
  UpdateCategoryInput,
  UpdateMenuItemInput,
} from '@cracked-mug/shared';
import { formatPrice } from '@cracked-mug/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/cn';
import {
  adminCreateCategory,
  adminCreateItem,
  adminDeleteCategory,
  adminDeleteItem,
  adminUpdateCategory,
  adminUpdateItem,
} from '../../services/adminApi';
import { fetchMenu } from '../../services/menuApi';
import { CategoryEditor } from './CategoryEditor';
import { ItemEditor } from './ItemEditor';

type Tab = 'categories' | 'items';

export function AdminMenuManager() {
  const [tab, setTab] = useState<Tab>('categories');
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [categoryEditOpen, setCategoryEditOpen] = useState(false);
  const [categoryEditing, setCategoryEditing] = useState<MenuCategory | null>(null);

  const [itemEditOpen, setItemEditOpen] = useState(false);
  const [itemEditing, setItemEditing] = useState<MenuItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMenu()
      .then((data) => {
        if (cancelled) return;
        setCategories(data.categories);
        setItems(data.items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load menu');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCategorySubmit = async (
    input: CreateCategoryInput | UpdateCategoryInput,
  ): Promise<void> => {
    if (categoryEditing) {
      const updated = await adminUpdateCategory(categoryEditing.categoryId, input);
      setCategories((prev) => prev.map((c) => (c.categoryId === updated.categoryId ? updated : c)));
    } else {
      const created = await adminCreateCategory(input as CreateCategoryInput);
      setCategories((prev) => [...prev, created]);
    }
  };

  const handleCategoryDelete = async (category: MenuCategory): Promise<void> => {
    const confirm = window.confirm(`Delete "${category.name}"? Items must be moved first.`);
    if (!confirm) return;
    try {
      await adminDeleteCategory(category.categoryId);
      setCategories((prev) => prev.filter((c) => c.categoryId !== category.categoryId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleItemSubmit = async (
    input: CreateMenuItemInput | UpdateMenuItemInput,
  ): Promise<void> => {
    if (itemEditing) {
      const updated = await adminUpdateItem(itemEditing.itemId, input);
      setItems((prev) => prev.map((i) => (i.itemId === updated.itemId ? updated : i)));
    } else {
      const created = await adminCreateItem(input as CreateMenuItemInput);
      setItems((prev) => [...prev, created]);
    }
  };

  const handleItemDelete = async (item: MenuItem): Promise<void> => {
    const confirm = window.confirm(`Delete "${item.name}"?`);
    if (!confirm) return;
    try {
      await adminDeleteItem(item.itemId);
      setItems((prev) => prev.filter((i) => i.itemId !== item.itemId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const openCategory = (category: MenuCategory | null): void => {
    setCategoryEditing(category);
    setCategoryEditOpen(true);
  };

  const openItem = (item: MenuItem | null): void => {
    setItemEditing(item);
    setItemEditOpen(true);
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl">Menu</h1>
        <div className="inline-flex rounded-full border border-wca-black/[0.06] bg-wca-gray-light p-1">
          <TabButton active={tab === 'categories'} onClick={() => setTab('categories')}>
            Categories ({categories.length})
          </TabButton>
          <TabButton active={tab === 'items'} onClick={() => setTab('items')}>
            Items ({items.length})
          </TabButton>
        </div>
      </header>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
      )}

      {isLoading ? (
        <p className="font-mono text-sm text-wca-gray-mid py-12">Loading menu…</p>
      ) : tab === 'categories' ? (
        <CategoriesTable
          categories={categories}
          onEdit={openCategory}
          onDelete={(c) => void handleCategoryDelete(c)}
          onAdd={() => openCategory(null)}
        />
      ) : (
        <ItemsTable
          items={items}
          categories={categories}
          onEdit={openItem}
          onDelete={(i) => void handleItemDelete(i)}
          onAdd={() => openItem(null)}
        />
      )}

      <CategoryEditor
        open={categoryEditOpen}
        category={categoryEditing}
        onClose={() => setCategoryEditOpen(false)}
        onSubmit={handleCategorySubmit}
      />
      <ItemEditor
        open={itemEditOpen}
        item={itemEditing}
        categories={categories}
        onClose={() => setItemEditOpen(false)}
        onSubmit={handleItemSubmit}
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-4 py-1.5 text-sm font-medium transition-all',
        active
          ? 'bg-wca-surface text-wca-black shadow-[0_1px_3px_rgba(26,26,26,0.12)]'
          : 'text-wca-gray-mid hover:text-wca-black',
      )}
    >
      {children}
    </button>
  );
}

function CategoriesTable({
  categories,
  onEdit,
  onDelete,
  onAdd,
}: {
  categories: MenuCategory[];
  onEdit: (c: MenuCategory) => void;
  onDelete: (c: MenuCategory) => void;
  onAdd: () => void;
}) {
  const sorted = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);
  return (
    <>
      <div className="flex justify-end">
        <Button onClick={onAdd} variant="primary">
          <Plus size={16} />
          New category
        </Button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-wca-gray-light bg-wca-chalk/60 text-left font-mono text-[11px] uppercase tracking-wider text-wca-gray-mid">
              <th className="px-4 py-3 font-semibold">Order</th>
              <th className="px-4 py-3 font-semibold">Icon</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr
                key={c.categoryId}
                className="border-b border-wca-gray-light last:border-b-0 transition-colors hover:bg-wca-chalk/50"
              >
                <td className="px-4 py-3 font-mono">{c.displayOrder}</td>
                <td className="px-4 py-3 font-mono">{c.icon}</td>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3">
                  {c.isActive ? (
                    <span className="text-xs font-mono uppercase tracking-wider text-wca-gold">
                      Active
                    </span>
                  ) : (
                    <span className="text-xs font-mono uppercase tracking-wider text-wca-gray-mid">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onEdit(c)}
                    aria-label={`Edit ${c.name}`}
                    className="text-wca-gray-mid hover:text-wca-black p-1.5"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(c)}
                    aria-label={`Delete ${c.name}`}
                    className="text-wca-gray-mid hover:text-wca-red p-1.5"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-wca-gray-mid">
                  No categories yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ItemsTable({
  items,
  categories,
  onEdit,
  onDelete,
  onAdd,
}: {
  items: MenuItem[];
  categories: MenuCategory[];
  onEdit: (i: MenuItem) => void;
  onDelete: (i: MenuItem) => void;
  onAdd: () => void;
}) {
  const catName = (id: string): string => categories.find((c) => c.categoryId === id)?.name ?? '—';
  const sorted = [...items].sort((a, b) => {
    const aCat = catName(a.categoryId);
    const bCat = catName(b.categoryId);
    if (aCat !== bCat) return aCat.localeCompare(bCat);
    return a.displayOrder - b.displayOrder;
  });
  return (
    <>
      <div className="flex justify-end">
        <Button onClick={onAdd} variant="primary" disabled={categories.length === 0}>
          <Plus size={16} />
          New item
        </Button>
      </div>
      {categories.length === 0 && (
        <p className="text-sm text-wca-gray-mid">Add a category first.</p>
      )}
      <div className="overflow-x-auto rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-wca-gray-light bg-wca-chalk/60 text-left font-mono text-[11px] uppercase tracking-wider text-wca-gray-mid">
              <th className="px-4 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Base price</th>
              <th className="px-4 py-3 font-semibold">Mods</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((item) => (
              <tr
                key={item.itemId}
                className="border-b border-wca-gray-light last:border-b-0 transition-colors hover:bg-wca-chalk/50"
              >
                <td className="px-4 py-3 text-wca-gray-mid">{catName(item.categoryId)}</td>
                <td className="px-4 py-3 font-medium">{item.name}</td>
                <td className="px-4 py-3 font-mono text-wca-red tabular-nums">
                  {formatPrice(item.basePriceCents)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-wca-gray-mid">
                  {item.customizationGroups.length} group
                  {item.customizationGroups.length === 1 ? '' : 's'}
                </td>
                <td className="px-4 py-3">
                  {!item.isAvailable && (
                    <span className="text-xs font-mono uppercase tracking-wider text-wca-gray-mid">
                      Sold out
                    </span>
                  )}
                  {item.isSeasonal && (
                    <span className="text-xs font-mono uppercase tracking-wider text-wca-gold ml-2">
                      Seasonal
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onEdit(item)}
                    aria-label={`Edit ${item.name}`}
                    className="text-wca-gray-mid hover:text-wca-black p-1.5"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(item)}
                    aria-label={`Delete ${item.name}`}
                    className="text-wca-gray-mid hover:text-wca-red p-1.5"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-wca-gray-mid">
                  No items yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
